import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useEvent } from '../hooks/useEvent';
import {
  createSession,
  updateSession,
  deleteSession as apiDeleteSession,
  vote,
  fetchProposalByDisplayId,
  toggleVoting,
  presentProposal,
} from '../lib/api';
import type { Proposal, Session, Template } from '../types';
import { HandIcon } from '../components/InterestCard';

const LIVE_REFRESH_MS = 4000;

/** Compute field names to hide because they have <=1 option in the template. */
function singleOptionFieldNames(template: Template | null): Set<string> {
  const hidden = new Set<string>();
  if (!template) return hidden;
  if ((template.languageOptions?.length ?? 0) <= 1) hidden.add('language');
  if ((template.formatOptions?.length ?? 0) <= 1) hidden.add('format');
  if ((template.levelOptions?.length ?? 0) <= 1) hidden.add('level');
  if ((template.tagOptions?.length ?? 0) <= 1) hidden.add('tags');
  for (const field of template.fields) {
    if (hidden.has(field.name)) continue;
    if (field.type === 'select' && (field.options?.length ?? 0) <= 1) hidden.add(field.name);
  }
  return hidden;
}

type FilterMode = 'all' | 'unprocessed' | 'assigned';
type PageView = 'cards' | 'sessions';

/** Helper: get display name(s) for a proposal's presenters */
function presenterNames(p: Proposal): string {
  if (p.people?.length) {
    return p.people.map((x) => x.nickname).join(', ');
  }
  return p.createdByNickname;
}

/**
 * Present SESSION CARDS during opening (e.g. on projector).
 * Organizers can present cards, audience can raise hand for interest; view updates live.
 * Create or combine sessions from session cards (search by ID, click-to-select, drag).
 * Manage sessions: edit, add/remove cards, delete.
 */
export function PresentPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    event,
    template,
    proposals,
    sessions,
    myVotes,
    hasAccessCode,
    isOrganizer,
    isLoading,
    refresh,
  } = useEvent(eventId ?? null);

  // Fields to hide in card details (single-option = no useful info)
  const hiddenFields = singleOptionFieldNames(template);

  // --- State: card selection & creation ---
  const [searchId, setSearchId] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createModal, setCreateModal] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [focusedProposal, setFocusedProposal] = useState<Proposal | null>(null);
  const [creatingDirectly, setCreatingDirectly] = useState<string | null>(null);
  const [expandedInCombine, setExpandedInCombine] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // --- State: session management ---
  const [pageView, setPageView] = useState<PageView>('cards');
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPeople, setEditPeople] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [addCardToSession, setAddCardToSession] = useState<Session | null>(null);
  const [addCardSearchId, setAddCardSearchId] = useState('');
  const [addCardError, setAddCardError] = useState<string | null>(null);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState<Session | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const [isTogglingVoting, setIsTogglingVoting] = useState(false);
  const [isPresentingSaving, setIsPresentingSaving] = useState(false);

  // For drag to selection area
  const selectionDropRef = useRef<HTMLDivElement>(null);

  const canVote = hasAccessCode || isOrganizer;
  const voteMap = new Map<string, number>();
  myVotes.forEach((v) => {
    if (v.targetType === 'proposal') voteMap.set(v.targetId, v.weight);
  });

  // Track which proposals are already assigned to sessions
  const assignedProposalIds = new Set(sessions.flatMap((s) => s.proposals.map((p) => p.id)));

  // Live auto-refresh
  useEffect(() => {
    const t = setInterval(() => refresh(), LIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Keep selectedIds in sync with live data: if a proposal was assigned externally, deselect it
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of prev) {
        if (assignedProposalIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // Derive selected proposals from ids (preserving order of selection)
  const selectedProposals = Array.from(selectedIds)
    .map((id) => (proposals ?? []).find((p) => p.id === id))
    .filter(Boolean) as Proposal[];

  // ---- Sorting & Filtering (must run before early returns to keep hook order stable) ----
  const sortedProposals = [...(proposals ?? [])].sort(
    (a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0)
  );

  const filteredProposals = sortedProposals.filter((p) => {
    if (filterMode === 'unprocessed') return !assignedProposalIds.has(p.id);
    if (filterMode === 'assigned') return assignedProposalIds.has(p.id);
    return true;
  });

  const unprocessedCount = sortedProposals.filter(
    (p) => !assignedProposalIds.has(p.id)
  ).length;
  const assignedCount = sortedProposals.filter((p) =>
    assignedProposalIds.has(p.id)
  ).length;

  // ---- Drag & drop handlers (useCallback hooks MUST be before early returns) ----
  const handleCardDragStart = useCallback(
    (e: React.DragEvent, proposalId: string) => {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', proposalId);
    },
    []
  );

  const handleSelectionDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleSelectionDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const proposalId = e.dataTransfer.getData('text/plain');
      if (proposalId && !assignedProposalIds.has(proposalId)) {
        setSelectedIds((prev) => new Set(prev).add(proposalId));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [assignedProposalIds]
  );

  // ---- Early returns (after all hooks) ----
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          <p className="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!eventId || !event) {
    return <Navigate to="/" replace />;
  }

  if (!isOrganizer) {
    return <Navigate to={`/event/${eventId}`} replace />;
  }

  // ---- Selection helpers ----
  const toggleSelect = (proposalId: string) => {
    if (assignedProposalIds.has(proposalId)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(proposalId)) {
        next.delete(proposalId);
      } else {
        next.add(proposalId);
      }
      return next;
    });
  };

  const removeSelected = (proposalId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(proposalId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ---- Card actions ----
  const handleVote = async (proposalId: string, weight: number) => {
    if (!canVote) return;
    try {
      await vote(eventId!, 'proposal', proposalId, weight);
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearchAdd = async () => {
    const id = searchId.trim();
    if (!id) return;
    setSearchError(null);
    try {
      const num = parseInt(id, 10);
      if (Number.isNaN(num)) {
        setSearchError('Enter a card number');
        return;
      }
      const p = await fetchProposalByDisplayId(eventId!, num);
      if (assignedProposalIds.has(p.id)) {
        setSearchError('Already assigned to a session');
        return;
      }
      if (selectedIds.has(p.id)) {
        setSearchError('Already selected');
        return;
      }
      setSelectedIds((prev) => new Set(prev).add(p.id));
      setSearchId('');
    } catch {
      setSearchError('Session card not found');
    }
  };

  /** Direct single-click: create session from one card immediately. */
  const handleDirectCreate = async (proposal: Proposal) => {
    if (assignedProposalIds.has(proposal.id)) return;
    setCreatingDirectly(proposal.id);
    try {
      await createSession(eventId!, {
        title: proposal.title,
        description: proposal.description || undefined,
        fields:
          proposal.fields && Object.keys(proposal.fields).length
            ? proposal.fields
            : undefined,
        proposalIds: [proposal.id],
        people: proposal.people?.length
          ? proposal.people.map((p) => ({
              nickname: p.nickname,
              role: p.role || 'presenter',
            }))
          : [{ nickname: proposal.createdByNickname, role: 'presenter' }],
        spanSlots: proposal.preferredSlotSpan ?? undefined,
      });
      removeSelected(proposal.id);
      if (focusedProposal?.id === proposal.id) setFocusedProposal(null);
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setCreatingDirectly(null);
    }
  };

  /** Multi-card combine via modal. */
  const handleCreateSession = async () => {
    if (selectedProposals.length === 0) return;
    const single =
      selectedProposals.length === 1 ? selectedProposals[0] : null;
    const title =
      sessionTitle.trim() ||
      (single
        ? single.title
        : selectedProposals.map((p) => p.title).join(' + '));
    const description =
      sessionDescription.trim() ||
      (single ? (single.description ?? '') : '');
    const fields =
      single && single.fields && Object.keys(single.fields).length
        ? single.fields
        : undefined;
    setSubmitting(true);
    try {
      const allPeople = new Map<
        string,
        { nickname: string; role: string }
      >();
      for (const p of selectedProposals) {
        if (p.people?.length) {
          for (const person of p.people) {
            if (!allPeople.has(person.nickname)) {
              allPeople.set(person.nickname, {
                nickname: person.nickname,
                role: person.role || 'presenter',
              });
            }
          }
        } else {
          if (!allPeople.has(p.createdByNickname)) {
            allPeople.set(p.createdByNickname, {
              nickname: p.createdByNickname,
              role: 'presenter',
            });
          }
        }
      }
      await createSession(eventId!, {
        title,
        description: description || undefined,
        fields,
        proposalIds: selectedProposals.map((p) => p.id),
        people: Array.from(allPeople.values()),
        spanSlots: single?.preferredSlotSpan ?? undefined,
      });
      clearSelection();
      setCreateModal(false);
      setSessionTitle('');
      setSessionDescription('');
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Session management actions ----
  const openEditSession = (session: Session) => {
    setEditingSession(session);
    setEditTitle(session.title);
    setEditDescription(session.description ?? '');
    setEditPeople(
      session.people.map((p) => `${p.nickname}${p.role === 'copresenter' ? ' (co)' : ''}`).join(', ')
    );
  };

  const handleSaveSession = async () => {
    if (!editingSession) return;
    setEditSubmitting(true);
    try {
      // Parse people string: "name1, name2 (co), name3"
      const people = editPeople
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
          const isCo = s.includes('(co)');
          const nickname = s.replace(/\s*\(co\)\s*/g, '').trim();
          return {
            nickname,
            role: isCo ? 'copresenter' : 'presenter',
          };
        });

      await updateSession(eventId!, editingSession.id, {
        title: editTitle.trim() || editingSession.title,
        description: editDescription.trim() || null,
        people: people.length > 0 ? people : undefined,
      });
      setEditingSession(null);
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleRemoveProposalFromSession = async (
    session: Session,
    proposalId: string
  ) => {
    const newIds = session.proposals
      .map((p) => p.id)
      .filter((id) => id !== proposalId);
    try {
      await updateSession(eventId!, session.id, { proposalIds: newIds });
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddCardToSessionSubmit = async () => {
    if (!addCardToSession) return;
    const id = addCardSearchId.trim();
    if (!id) return;
    setAddCardError(null);
    try {
      const num = parseInt(id, 10);
      if (Number.isNaN(num)) {
        setAddCardError('Enter a card number');
        return;
      }
      const p = await fetchProposalByDisplayId(eventId!, num);
      // Check if already assigned to a different session
      const alreadyInOtherSession = sessions.some(
        (s) =>
          s.id !== addCardToSession.id &&
          s.proposals.some((sp) => sp.id === p.id)
      );
      if (alreadyInOtherSession) {
        setAddCardError('Card is already assigned to another session');
        return;
      }
      // Check if already in this session
      if (addCardToSession.proposals.some((sp) => sp.id === p.id)) {
        setAddCardError('Card is already in this session');
        return;
      }
      const newIds = [...addCardToSession.proposals.map((sp) => sp.id), p.id];
      await updateSession(eventId!, addCardToSession.id, {
        proposalIds: newIds,
      });
      setAddCardSearchId('');
      setAddCardToSession(null);
      refresh();
    } catch {
      setAddCardError('Session card not found');
    }
  };

  const handleDeleteSession = async () => {
    if (!confirmDeleteSession) return;
    setDeletingSession(true);
    try {
      await apiDeleteSession(eventId!, confirmDeleteSession.id);
      setConfirmDeleteSession(null);
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingSession(false);
    }
  };

  // Sort sessions by creation date (newest first) or by vote count
  const sortedSessions = [...sessions].sort(
    (a, b) =>
      ((b.proposalVoteCount ?? 0) + (b.voteCount ?? 0)) -
      ((a.proposalVoteCount ?? 0) + (a.voteCount ?? 0))
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">{event.name}</h1>
            <p className="mt-1" style={{ color: '#F7C23D' }}>
              Session cards &mdash; present &amp; gauge interest
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Interest toggle (was "voting") */}
            <button
              type="button"
              onClick={async () => {
                setIsTogglingVoting(true);
                try {
                  await toggleVoting(eventId!, !event.votingOpen);
                  refresh();
                } finally {
                  setIsTogglingVoting(false);
                }
              }}
              disabled={isTogglingVoting}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
              style={
                event.votingOpen
                  ? { background: '#22c55e', color: '#fff' }
                  : { background: 'rgba(239,68,68,0.8)', color: '#fff' }
              }
            >
              {isTogglingVoting
                ? 'Saving...'
                : event.votingOpen
                  ? 'Interest OPEN (click to close)'
                  : 'Interest CLOSED (click to open)'}
            </button>
            {/* Stop presenting */}
            {event.presentingProposalId && (
              <button
                type="button"
                onClick={async () => {
                  setIsPresentingSaving(true);
                  try {
                    await presentProposal(eventId!, null);
                    refresh();
                  } finally {
                    setIsPresentingSaving(false);
                  }
                }}
                disabled={isPresentingSaving}
                className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}
              >
                Stop Presenting
              </button>
            )}
            <Link
              to={`/event/${eventId}/admin`}
              className="text-gray-500 hover:text-white text-sm"
            >
              &larr; Admin
            </Link>
          </div>
        </div>

        {/* Currently presenting card banner */}
        {event.presentingProposalId && (() => {
          const liveProposal = proposals.find((p) => p.id === event.presentingProposalId);
          if (!liveProposal) return null;
          return (
            <div
              className="mb-6 p-4 flex items-center gap-4 justify-between"
              style={{
                background: 'rgba(34,197,94,0.1)',
                border: '2px solid rgba(34,197,94,0.3)',
                borderRadius: '16px',
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="flex-shrink-0 w-3 h-3 rounded-full animate-pulse"
                  style={{ background: '#22c55e' }}
                />
                <div className="min-w-0">
                  <p className="text-xs text-green-400 font-semibold uppercase tracking-wide">Now Presenting</p>
                  <p className="text-white font-bold truncate">
                    <span className="text-sm font-mono mr-1" style={{ color: '#F7C23D' }}>
                      #{liveProposal.displayId ?? '--'}
                    </span>
                    {liveProposal.title}
                  </p>
                  <p className="text-gray-400 text-sm truncate">{presenterNames(liveProposal)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-lg font-bold tabular-nums inline-flex items-center gap-1" style={{ color: '#F7C23D' }}>
                  <HandIcon className="w-5 h-5" /> {liveProposal.voteCount ?? 0}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    setIsPresentingSaving(true);
                    try {
                      await presentProposal(eventId!, null);
                      refresh();
                    } finally {
                      setIsPresentingSaving(false);
                    }
                  }}
                  disabled={isPresentingSaving}
                  className="px-3 py-1.5 rounded text-xs font-semibold"
                  style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}
                >
                  Stop
                </button>
              </div>
            </div>
          );
        })()}

        {/* View toggle: Cards / Sessions */}
        <div className="flex items-center gap-2 mb-6">
          <ViewToggle
            active={pageView === 'cards'}
            onClick={() => setPageView('cards')}
          >
            Session Cards ({sortedProposals.length})
          </ViewToggle>
          <ViewToggle
            active={pageView === 'sessions'}
            onClick={() => setPageView('sessions')}
          >
            Sessions ({sessions.length})
          </ViewToggle>
        </div>

        {/* ========== CARDS VIEW ========== */}
        {pageView === 'cards' && (
          <>
            {/* Selected cards area + search (drop target) */}
            <div
              ref={selectionDropRef}
              className="mb-6 p-4 rounded-lg"
              style={{
                background:
                  selectedIds.size > 0
                    ? 'rgba(247,194,61,0.08)'
                    : 'rgba(255,255,255,0.04)',
                border:
                  selectedIds.size > 0
                    ? '2px solid rgba(247,194,61,0.3)'
                    : '2px dashed rgba(255,255,255,0.1)',
                transition: 'all 0.2s',
              }}
              onDragOver={handleSelectionDragOver}
              onDrop={handleSelectionDrop}
            >
              {/* Search row */}
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <div>
                  <label
                    htmlFor="search-id"
                    className="block text-sm text-gray-400 mb-1"
                  >
                    Add session card by number
                  </label>
                  <input
                    id="search-id"
                    type="number"
                    min={1}
                    className="bg-gray-700 border border-gray-600 rounded px-3 py-2 w-24 text-white"
                    placeholder="#"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchAdd()}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearchAdd}
                  className="btn btn-primary"
                >
                  Add
                </button>
                {searchError && (
                  <span className="text-red-400 text-sm">{searchError}</span>
                )}
                <span className="text-gray-500 text-xs ml-auto">
                  or click / drag cards below to select
                </span>
              </div>

              {/* Selected cards strip */}
              {selectedProposals.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedProposals.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm"
                        style={{
                          background: 'rgba(247,194,61,0.15)',
                          border: '1px solid rgba(247,194,61,0.3)',
                        }}
                      >
                        <span
                          className="font-bold tabular-nums"
                          style={{ color: '#F7C23D' }}
                        >
                          #{p.displayId ?? '--'}
                        </span>
                        <span className="text-white truncate max-w-[180px]">
                          {p.title}
                        </span>
                        <span className="text-gray-400 text-xs">
                          ({p.voteCount})
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSelected(p.id)}
                          className="text-gray-400 hover:text-white ml-1"
                          aria-label={`Remove ${p.title}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProposals.length === 1 ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleDirectCreate(selectedProposals[0])
                        }
                        disabled={
                          creatingDirectly === selectedProposals[0].id
                        }
                        className="btn btn-primary"
                      >
                        {creatingDirectly === selectedProposals[0].id
                          ? 'Creating...'
                          : 'Create session'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setSessionTitle(
                            selectedProposals
                              .map((p) => p.title)
                              .join(' + ')
                          );
                          setSessionDescription('');
                          setCreateModal(true);
                        }}
                        className="btn btn-primary"
                      >
                        Combine {selectedProposals.length} cards into session
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="btn btn-ghost text-gray-400"
                    >
                      Clear
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm">
                  No cards selected. Click or drag session cards here to build
                  a session.
                </p>
              )}
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-1 mb-4">
              <FilterButton
                active={filterMode === 'all'}
                onClick={() => setFilterMode('all')}
              >
                All ({sortedProposals.length})
              </FilterButton>
              <FilterButton
                active={filterMode === 'unprocessed'}
                onClick={() => setFilterMode('unprocessed')}
              >
                Unprocessed ({unprocessedCount})
              </FilterButton>
              <FilterButton
                active={filterMode === 'assigned'}
                onClick={() => setFilterMode('assigned')}
              >
                Assigned ({assignedCount})
              </FilterButton>
            </div>

            {/* Session cards grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProposals.map((p) => {
                const isAssigned = assignedProposalIds.has(p.id);
                const isSelected = selectedIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`p-4 flex flex-col transition-all ${
                      isAssigned ? 'opacity-50' : 'cursor-pointer'
                    }`}
                    style={{
                      background: isSelected
                        ? 'rgba(247,194,61,0.1)'
                        : 'rgba(255,255,255,0.04)',
                      borderRadius: '16px',
                      border: isSelected
                        ? '2px solid rgba(247,194,61,0.5)'
                        : isAssigned
                          ? '1px solid rgba(161,212,82,0.4)'
                          : '1px solid rgba(255,255,255,0.1)',
                    }}
                    draggable={!isAssigned}
                    onDragStart={(e) =>
                      !isAssigned && handleCardDragStart(e, p.id)
                    }
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!isAssigned) toggleSelect(p.id);
                    }}
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === ' ') &&
                        !isAssigned
                      ) {
                        e.preventDefault();
                        toggleSelect(p.id);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center text-xs font-bold"
                          style={
                            isSelected
                              ? { background: '#F7C23D', color: '#000' }
                              : isAssigned
                                ? {
                                    background: 'rgba(161,212,82,0.2)',
                                    color: '#A1D452',
                                  }
                                : {
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'transparent',
                                  }
                          }
                        >
                          {isSelected ? '✓' : isAssigned ? '✓' : ''}
                        </span>
                        <span
                          className="text-lg font-bold tabular-nums"
                          style={{ color: '#F7C23D' }}
                        >
                          #{p.displayId ?? '--'}
                        </span>
                        {isAssigned && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(161,212,82,0.2)',
                              color: '#A1D452',
                            }}
                          >
                            assigned
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFocusedProposal(p);
                          }}
                          className="px-2 py-1 rounded text-xs"
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            color: 'rgba(255,255,255,0.6)',
                          }}
                          aria-label="Show details"
                          title="Show details"
                        >
                          Details
                        </button>
                        {/* Present to Audience */}
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            setIsPresentingSaving(true);
                            try {
                              await presentProposal(eventId!, p.id);
                              refresh();
                            } finally {
                              setIsPresentingSaving(false);
                            }
                          }}
                          disabled={isPresentingSaving}
                          className="px-2 py-1 rounded text-xs font-semibold"
                          style={
                            event.presentingProposalId === p.id
                              ? { background: '#22c55e', color: '#fff' }
                              : {
                                  background: 'rgba(255,255,255,0.08)',
                                  color: 'rgba(255,255,255,0.6)',
                                }
                          }
                          aria-label={
                            event.presentingProposalId === p.id
                              ? 'Currently presenting'
                              : 'Present to audience'
                          }
                          title={
                            event.presentingProposalId === p.id
                              ? 'Currently presenting to audience'
                              : 'Present to audience'
                          }
                        >
                          {event.presentingProposalId === p.id ? '● Live' : '▶ Present'}
                        </button>
                        {/* Interest count */}
                        <span
                          className="px-2 py-1 rounded text-xs font-semibold tabular-nums"
                          style={{ color: '#F7C23D' }}
                          title={`${p.voteCount ?? 0} raised hands`}
                        >
                          <HandIcon className="w-3.5 h-3.5 inline" /> {p.voteCount ?? 0}
                        </span>
                      </div>
                    </div>
                    <h2 className="text-lg font-bold text-white mt-1 leading-tight">
                      {p.title}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">
                      {presenterNames(p)}
                    </p>
                    {!canVote && (
                      <p className="text-gray-500 text-sm mt-2">
                        {p.voteCount ?? 0} interested
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredProposals.length === 0 && (
              <p className="text-gray-500 text-center py-12">
                {filterMode === 'all'
                  ? 'No session cards yet.'
                  : filterMode === 'unprocessed'
                    ? 'All cards have been assigned!'
                    : 'No assigned cards yet.'}
              </p>
            )}
          </>
        )}

        {/* ========== SESSIONS VIEW ========== */}
        {pageView === 'sessions' && (
          <>
            {sortedSessions.length === 0 ? (
              <p className="text-gray-500 text-center py-12">
                No sessions created yet. Switch to Session Cards to create
                sessions from cards.
              </p>
            ) : (
              <div className="space-y-4">
                {sortedSessions.map((session) => {
                  const interest =
                    (session.proposalVoteCount ?? 0) +
                    (session.voteCount ?? 0);
                  return (
                    <div
                      key={session.id}
                      className="p-4 rounded-lg"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '16px',
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-white leading-tight">
                            {session.title}
                          </h3>
                          {session.people.length > 0 && (
                            <p className="text-gray-400 text-sm mt-1">
                              {session.people
                                .map(
                                  (p) =>
                                    p.nickname +
                                    (p.role === 'copresenter'
                                      ? ' (co)'
                                      : '')
                                )
                                .join(', ')}
                            </p>
                          )}
                          {session.description && (
                            <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                              {session.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {interest > 0 && (
                            <span
                              className="text-sm font-bold px-2 py-1 rounded"
                              style={{
                                background: 'rgba(247,194,61,0.15)',
                                color: '#F7C23D',
                              }}
                            >
                              <HandIcon className="w-3.5 h-3.5 inline" /> {interest}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => openEditSession(session)}
                            className="px-2 py-1 rounded text-xs"
                            style={{
                              background: 'rgba(255,255,255,0.08)',
                              color: 'rgba(255,255,255,0.6)',
                            }}
                            title="Edit session"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmDeleteSession(session)
                            }
                            className="px-2 py-1 rounded text-xs"
                            style={{
                              background: 'rgba(239,68,68,0.1)',
                              color: 'rgba(239,68,68,0.7)',
                            }}
                            title="Delete session"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Session cards (proposals) in this session */}
                      {session.proposals.length > 0 && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">
                              Session cards ({session.proposals.length})
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setAddCardToSession(session);
                                setAddCardSearchId('');
                                setAddCardError(null);
                              }}
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(247,194,61,0.1)',
                                color: '#F7C23D',
                              }}
                            >
                              + Add card
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {session.proposals.map((sp) => {
                              // Find the full proposal for extra info
                              const fullProposal = (proposals ?? []).find(
                                (p) => p.id === sp.id
                              );
                              return (
                                <div
                                  key={sp.id}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded text-sm"
                                  style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border:
                                      '1px solid rgba(255,255,255,0.1)',
                                  }}
                                >
                                  <span
                                    className="font-bold tabular-nums"
                                    style={{ color: '#F7C23D' }}
                                  >
                                    #
                                    {fullProposal?.displayId ??
                                      '--'}
                                  </span>
                                  <span className="text-white truncate max-w-[200px]">
                                    {sp.title}
                                  </span>
                                  <span className="text-gray-500 text-xs">
                                    by {sp.createdByNickname}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveProposalFromSession(
                                        session,
                                        sp.id
                                      )
                                    }
                                    className="text-gray-500 hover:text-red-400 ml-1"
                                    title="Remove card from session"
                                    aria-label={`Remove ${sp.title} from session`}
                                  >
                                    &times;
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {session.proposals.length === 0 && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              No session cards linked
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setAddCardToSession(session);
                                setAddCardSearchId('');
                                setAddCardError(null);
                              }}
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(247,194,61,0.1)',
                                color: '#F7C23D',
                              }}
                            >
                              + Add card
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ========== MODALS ========== */}

        {/* Card detail overlay */}
        {focusedProposal && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setFocusedProposal(null)}
          >
            <div
              className="max-w-2xl w-full p-8 shadow-2xl"
              style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                border: '1px solid rgba(247,194,61,0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <span
                    className="text-2xl font-bold tabular-nums"
                    style={{ color: '#F7C23D' }}
                  >
                    #{focusedProposal.displayId ?? '--'}
                  </span>
                  {assignedProposalIds.has(focusedProposal.id) && (
                    <span
                      className="text-xs px-2 py-0.5 rounded ml-3"
                      style={{
                        background: 'rgba(161,212,82,0.2)',
                        color: '#A1D452',
                      }}
                    >
                      already assigned to session
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFocusedProposal(null)}
                  className="text-gray-400 hover:text-white text-2xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <h2 className="text-3xl font-bold text-white leading-tight mb-3">
                {focusedProposal.title}
              </h2>
              <p className="text-lg text-gray-300 mb-4">
                by {presenterNames(focusedProposal)}
              </p>
              {focusedProposal.description && (
                <div className="mb-4">
                  <p className="text-gray-300 whitespace-pre-wrap text-lg leading-relaxed">
                    {focusedProposal.description}
                  </p>
                </div>
              )}
              {focusedProposal.fields &&
                Object.entries(focusedProposal.fields).some(([k, v]) => v && !hiddenFields.has(k)) && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(focusedProposal.fields).map(
                      ([k, v]) =>
                        v && !hiddenFields.has(k) && (
                          <span
                            key={k}
                            className="px-3 py-1 rounded text-sm"
                            style={{
                              background: 'rgba(255,255,255,0.08)',
                              color: 'rgba(255,255,255,0.7)',
                            }}
                          >
                            {k}: {v}
                          </span>
                        )
                    )}
                  </div>
                )}
              {focusedProposal.preferredSlotSpan && (
                <p className="text-sm text-gray-400 mb-4">
                  Preferred length: {focusedProposal.preferredSlotSpan} slot
                  {focusedProposal.preferredSlotSpan !== 1 ? 's' : ''}
                </p>
              )}
              <div
                className="flex items-center gap-4 pt-4"
                style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-white">
                    {focusedProposal.voteCount ?? 0}
                  </span>
                  <span className="text-gray-400 text-lg">interested</span>
                </div>
                <div className="flex-1" />
                {canVote && (
                  <button
                    type="button"
                    onClick={() =>
                      handleVote(
                        focusedProposal.id,
                        (voteMap.get(focusedProposal.id) ?? 0) > 0 ? 0 : 1
                      )
                    }
                    className="px-4 py-2 rounded text-sm font-semibold"
                    style={
                      (voteMap.get(focusedProposal.id) ?? 0) > 0
                        ? { backgroundColor: '#F7C23D', color: '#000' }
                        : {
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            color: 'rgba(255,255,255,0.7)',
                          }
                    }
                  >
                    {(voteMap.get(focusedProposal.id) ?? 0) > 0
                      ? 'Hand Raised'
                      : 'Raise Hand'}
                  </button>
                )}
                {!assignedProposalIds.has(focusedProposal.id) && (
                  <>
                    {!selectedIds.has(focusedProposal.id) ? (
                      <button
                        type="button"
                        onClick={() => {
                          toggleSelect(focusedProposal.id);
                          setFocusedProposal(null);
                        }}
                        className="btn btn-secondary"
                      >
                        Select
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          removeSelected(focusedProposal.id);
                          setFocusedProposal(null);
                        }}
                        className="btn btn-ghost text-gray-400"
                      >
                        Deselect
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDirectCreate(focusedProposal)}
                      disabled={creatingDirectly === focusedProposal.id}
                      className="btn btn-primary"
                    >
                      {creatingDirectly === focusedProposal.id
                        ? 'Creating...'
                        : 'Create session'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Combine modal */}
        {createModal && selectedProposals.length > 0 && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div
              className="max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
              style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                border: '1px solid rgba(247,194,61,0.25)',
              }}
            >
              <h3 className="text-xl font-bold mb-4">
                Combine {selectedProposals.length} Session Cards into Session
              </h3>
              <div className="space-y-2 mb-4">
                {selectedProposals.map((p) => (
                  <div
                    key={p.id}
                    className="overflow-hidden"
                    style={{
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '8px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedInCombine(
                          expandedInCombine === p.id ? null : p.id
                        )
                      }
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-sm"
                      style={{ background: 'rgba(255,255,255,0.03)' }}
                    >
                      <span>
                        <strong style={{ color: '#F7C23D' }}>
                          #{p.displayId ?? '--'}
                        </strong>{' '}
                        {p.title}
                        <span className="text-gray-500 ml-2 inline-flex items-center gap-0.5">
                          <HandIcon className="w-3.5 h-3.5" /> {p.voteCount}
                        </span>
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transform transition-transform ${expandedInCombine === p.id ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                    {expandedInCombine === p.id && (
                      <div
                        className="px-3 pb-3 space-y-2 text-sm"
                        style={{
                          borderTop: '1px solid rgba(255,255,255,0.08)',
                          background: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        <p className="text-gray-400 mt-2">
                          by {presenterNames(p)}
                        </p>
                        {p.description && (
                          <div>
                            <p className="text-gray-300 whitespace-pre-wrap">
                              {p.description}
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                setSessionDescription(p.description ?? '')
                              }
                              className="text-xs mt-1 font-semibold"
                              style={{ color: '#F7C23D' }}
                            >
                              Use this description
                            </button>
                          </div>
                        )}
                        {p.fields &&
                          Object.entries(p.fields).some(([k, v]) => v && !hiddenFields.has(k)) && (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(p.fields).map(
                                ([k, v]) =>
                                  v && !hiddenFields.has(k) && (
                                    <span
                                      key={k}
                                      className="px-2 py-0.5 rounded text-xs"
                                      style={{
                                        background:
                                          'rgba(255,255,255,0.08)',
                                        color: 'rgba(255,255,255,0.5)',
                                      }}
                                    >
                                      {k}: {v}
                                    </span>
                                  )
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    value={sessionTitle}
                    onChange={(e) => setSessionTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Description
                  </label>
                  <textarea
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white min-h-[80px]"
                    value={sessionDescription}
                    onChange={(e) => setSessionDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setCreateModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateSession}
                  disabled={submitting}
                  className="btn btn-primary flex-1"
                >
                  {submitting ? 'Creating...' : 'Create session'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit session modal */}
        {editingSession && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setEditingSession(null)}
          >
            <div
              className="max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
              style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                border: '1px solid rgba(247,194,61,0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Edit Session</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Description
                  </label>
                  <textarea
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white min-h-[80px]"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    People{' '}
                    <span className="text-gray-600">
                      (comma-separated; add &quot;(co)&quot; for co-presenters)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    value={editPeople}
                    onChange={(e) => setEditPeople(e.target.value)}
                    placeholder="Alice, Bob (co)"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingSession(null)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSession}
                  disabled={editSubmitting}
                  className="btn btn-primary flex-1"
                >
                  {editSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add card to session modal */}
        {addCardToSession && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setAddCardToSession(null)}
          >
            <div
              className="max-w-md w-full p-6"
              style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                border: '1px solid rgba(247,194,61,0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-1">
                Add Session Card to &ldquo;{addCardToSession.title}&rdquo;
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Enter the card number to add it to this session.
              </p>

              {/* Show unassigned cards as quick-add options */}
              {(() => {
                const unassignedCards = (proposals ?? []).filter(
                  (p) =>
                    !assignedProposalIds.has(p.id) ||
                    addCardToSession.proposals.some((sp) => sp.id === p.id)
                ).filter(
                  (p) => !addCardToSession.proposals.some((sp) => sp.id === p.id)
                );
                if (unassignedCards.length > 0 && unassignedCards.length <= 20) {
                  return (
                    <div className="mb-4 max-h-[200px] overflow-y-auto space-y-1">
                      <p className="text-xs text-gray-500 mb-1">Quick add unassigned cards:</p>
                      {unassignedCards
                        .sort((a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0))
                        .map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-1.5 rounded text-sm text-left hover:bg-white/5 transition-colors"
                            style={{
                              border: '1px solid rgba(255,255,255,0.08)',
                            }}
                            onClick={async () => {
                              const newIds = [
                                ...addCardToSession.proposals.map((sp) => sp.id),
                                p.id,
                              ];
                              try {
                                await updateSession(eventId!, addCardToSession.id, {
                                  proposalIds: newIds,
                                });
                                setAddCardToSession(null);
                                refresh();
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                          >
                            <span
                              className="font-bold tabular-nums"
                              style={{ color: '#F7C23D' }}
                            >
                              #{p.displayId ?? '--'}
                            </span>
                            <span className="text-white truncate flex-1">
                              {p.title}
                            </span>
                            <span className="text-gray-500 text-xs inline-flex items-center gap-0.5">
                              <HandIcon className="w-3 h-3" /> {p.voteCount}
                            </span>
                          </button>
                        ))}
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label
                    htmlFor="add-card-id"
                    className="block text-sm text-gray-400 mb-1"
                  >
                    Card number
                  </label>
                  <input
                    id="add-card-id"
                    type="number"
                    min={1}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    placeholder="#"
                    value={addCardSearchId}
                    onChange={(e) => setAddCardSearchId(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && handleAddCardToSessionSubmit()
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddCardToSessionSubmit}
                  className="btn btn-primary"
                >
                  Add
                </button>
              </div>
              {addCardError && (
                <p className="text-red-400 text-sm mt-2">{addCardError}</p>
              )}
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setAddCardToSession(null)}
                  className="btn btn-secondary flex-1"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete session confirm modal */}
        {confirmDeleteSession && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmDeleteSession(null)}
          >
            <div
              className="max-w-sm w-full p-6"
              style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                border: '1px solid rgba(239,68,68,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-2 text-red-400">
                Delete Session?
              </h3>
              <p className="text-gray-400 text-sm mb-1">
                &ldquo;{confirmDeleteSession.title}&rdquo;
              </p>
              <p className="text-gray-500 text-sm mb-4">
                This will free up{' '}
                {confirmDeleteSession.proposals.length > 0
                  ? `${confirmDeleteSession.proposals.length} session card${confirmDeleteSession.proposals.length !== 1 ? 's' : ''}`
                  : 'no session cards'}
                {' '}for re-assignment.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteSession(null)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSession}
                  disabled={deletingSession}
                  className="flex-1 px-4 py-2 rounded font-semibold text-sm"
                  style={{
                    background: deletingSession
                      ? 'rgba(239,68,68,0.3)'
                      : 'rgba(239,68,68,0.8)',
                    color: '#fff',
                  }}
                >
                  {deletingSession ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Pill-style filter button for the grid view mode. */
function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded text-sm font-semibold transition-colors"
      style={
        active
          ? {
              background: 'rgba(247,194,61,0.2)',
              color: '#F7C23D',
              border: '1px solid rgba(247,194,61,0.3)',
            }
          : {
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
            }
      }
    >
      {children}
    </button>
  );
}

/** Top-level view toggle between Cards and Sessions. */
function ViewToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
      style={
        active
          ? {
              background: '#F7C23D',
              color: '#000',
            }
          : {
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
            }
      }
    >
      {children}
    </button>
  );
}
